import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import clsx from 'clsx';
import { boostApi } from '@/lib/api';

const CITIES = [
  { id: 1, name: 'Toronto' },
  { id: 2, name: 'Montréal' },
  { id: 3, name: 'Vancouver' },
] as const;

const SLOTS = [
  { code: 'tonight_carousel', display: 'Tonight Carousel' },
  { code: 'event_boost', display: 'Event Boost' },
  { code: 'map_pin_boost', display: 'Map Pin' },
  { code: 'dj_featured', display: 'DJ Featured' },
  { code: 'this_weekend_carousel', display: 'This Weekend' },
] as const;

export function CalendarPage() {
  const [cityId, setCityId] = useState<number>(CITIES[0].id);
  const [slotCode, setSlotCode] = useState<string>(SLOTS[0].code);

  const calendarQ = useQuery({
    queryKey: ['boost', 'calendar', cityId, slotCode],
    queryFn: () => boostApi.calendar(cityId, slotCode, 14),
  });

  const cells = calendarQ.data?.cells ?? [];

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Calendar</h1>
        <p className="mt-1 text-text-secondary">Next 14 nights · slot availability</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <div>
          <label className="label mb-1 block">City</label>
          <select
            value={cityId}
            onChange={(e) => setCityId(Number(e.target.value))}
            className="input pr-9"
          >
            {CITIES.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label mb-1 block">Slot type</label>
          <select
            value={slotCode}
            onChange={(e) => setSlotCode(e.target.value)}
            className="input pr-9"
          >
            {SLOTS.map((s) => (
              <option key={s.code} value={s.code}>{s.display}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 md:grid-cols-7">
        {cells.map((c) => {
          const date = new Date(c.starts_at);
          const usedRatio = 1 - c.available / c.max_inventory;
          return (
            <div
              key={c.date}
              className={clsx(
                'rounded-2xl border p-4 transition-colors',
                c.is_sold_out
                  ? 'border-danger/40 bg-danger/10'
                  : usedRatio > 0.6
                    ? 'border-featured/40 bg-featured/5'
                    : 'border-border bg-surface',
              )}
            >
              <div className="text-xs uppercase tracking-wide text-text-muted">
                {format(date, 'EEE')}
              </div>
              <div className="mt-1 text-lg font-bold">{format(date, 'MMM d')}</div>
              <div className="mt-3 text-sm">
                {c.is_sold_out ? (
                  <span className="font-semibold text-danger">Sold out</span>
                ) : (
                  <>
                    <span className="text-2xl font-bold tabular-nums">{c.available}</span>
                    <span className="ml-1 text-text-muted">/ {c.max_inventory}</span>
                  </>
                )}
              </div>
              <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-border-subtle">
                <div
                  className={clsx(
                    'h-full',
                    c.is_sold_out
                      ? 'bg-danger'
                      : usedRatio > 0.6
                        ? 'bg-featured'
                        : 'bg-brand-500',
                  )}
                  style={{ width: `${Math.max(8, usedRatio * 100)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
